<script setup>
/**
 * 牌九「上分申请 / 下分申请」弹窗（充值 / 提现），逐条对齐旧版
 * web/19.0d8589924b85d555a0a0.js 里的 app-money 组件
 * （类 M、模板工厂 Gn + 子模板 Jn/Tn/Dn/$n/Bn/jn/Fn/An/Nn/Rn/En、样式 zn）。
 *
 * 旧版打开点：右侧菜单「充值」/「提现」→ moneyModal(type)，type 默认 1
 *   modalCtrl.create({ component: M, componentProps: { game, type, user } })
 *
 * 标题在旧版模板里是拆开拼的：
 *   ion-title > *ngIf="1==type" → "上" / *ngIf="2==type" → "下" + 文本 "分申请 "
 * 「上分方式：」「上分金额：」同理，所以在弹窗里切换交易类型，整套文案都会跟着变。
 *
 * 旧版类 M：
 *   ngOnInit: formGroup = fb.group({
 *               type:  [type.toString(), [required, min(1)]],
 *               bank:  ['',              [required, min(1)]],
 *               money: ['',              [required, min(1)]] })
 *             getBankList()
 *   getBankList(): bank 置空；this.type = formGroup.controls.type.value
 *     type==1 → GET /recharge/bank  （充值方式，即收款账户）
 *     type!=1 → GET /bank/lists     （提现用的本人银行卡）
 *     充值那条失败时错误文案被写死成「系统错误，请联系管理员」，提现那条用接口返回的 error_msg 并把 bankList 清空
 *   changBank(): 在 bankList 里按 bank_id 找出 selectedBank
 *   saveData():
 *     formGroup.invalid 直接 return
 *     loading「正在提交，请稍等」
 *     type==1 → createRecharge(bank, money) → POST /recharge/create {money, bank}
 *               成功（error_code===0 且 data.length>0）→ toast「充值申请提交成功」+ dismiss()
 *     type!=1 → 先判 parseFloat(money) > user.balance → toast「余额不足」并中止
 *               否则 createCash(bank, money) → POST /cash/create {money, bank}
 *               成功 → toast「提现申请提交成功」+ dismiss()
 */
import { computed, onMounted, ref } from 'vue'
import { request } from '../services/api'
import LegacySelect from './LegacySelect.vue'

const props = defineProps({
  // 旧版 navParams.get('game')，这个弹窗其实没用到 game，保留是为了跟旧版参数一致
  game: { type: Object, default: () => ({}) },
  // 旧版 navParams.get('type')：1 充值、2 提现
  type: { type: [String, Number], default: 1 },
  // 旧版 navParams.get('user')，提现时要用 user.balance 做余额校验
  user: { type: Object, default: () => ({ balance: 0 }) },
})
const emit = defineEmits(['close'])

// 旧版 formGroup 的三个控件。type 在 formGroup 里是字符串（type.toString()）
const formType = ref(String(props.type || 1))
const bank = ref('')
const money = ref('')
const bankList = ref([])
const selectedBank = ref(null)
const submitting = ref(false)
const notice = ref('')

// 旧版模板用 1==type / 2==type 宽松比较，字符串 '1' == 1 成立
const isRecharge = computed(() => Number(formType.value) === 1)
// 「上」/「下」，对应旧版 Jn/Tn、Dn/$n、Rn/En 这几个只输出一个字的子模板
const directionWord = computed(() => (isRecharge.value ? '上' : '下'))

// 旧版 DecimalPipe '.2-2'
const decimal2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
function fmt2(value) { return decimal2.format(Number(value || 0)) }

// 旧版交易类型的两个 ion-select-option
const typeOptions = [
  { value: '1', label: '充值' },
  { value: '2', label: '提现' },
]
// 旧版 jn → ngFor bankList → Bn：ion-select-option [value]="bank_id" 显示 bank_title
const bankOptions = computed(() => bankList.value.map((item) => ({ value: String(item.bank_id), label: item.bank_title })))

/** 旧版 [disabled]="0==bankList.length" */
const bankDisabled = computed(() => bankList.value.length === 0)

/**
 * 旧版 ion-button 的 [disabled]="!formGroup.valid"。
 * 三个控件都是 required + min(1)：type、bank 要 >= 1，money 也要 >= 1。
 */
const formValid = computed(() => {
  if (!(Number(formType.value) >= 1)) return false
  if (!(Number(bank.value) >= 1)) return false
  return Number(money.value) >= 1
})

/** 旧版 Nn：*ngIf="2==type && user.balance>0" 的「可用金额」行 */
const showBalance = computed(() => !isRecharge.value && Number(props.user?.balance) > 0)

/** 旧版 getBankList() */
async function getBankList() {
  // 旧版第一件事就是把 bank 清空
  bank.value = ''
  selectedBank.value = null
  notice.value = ''
  const recharge = isRecharge.value
  try {
    const response = await request(recharge ? '/recharge/bank' : '/bank/lists')
    const data = response.data || []
    if (data.length > 0) {
      bankList.value = data
    } else if (recharge) {
      // 旧版这里的文案是写死的，不用接口返回的 error_msg
      notice.value = '系统错误，请联系管理员'
    } else {
      bankList.value = []
    }
  } catch (error) {
    if (recharge) notice.value = '系统错误，请联系管理员'
    else { notice.value = error.message || '加载失败'; bankList.value = [] }
  }
}

/** 旧版 ion-select(type) 的 ionChange → getBankList() */
function onTypeChange() { void getBankList() }

/** 旧版 changBank() */
function changBank() {
  selectedBank.value = bankList.value.find((item) => String(item.bank_id) === String(bank.value)) || null
}

/** 旧版 saveData() */
async function saveData() {
  if (!formValid.value || submitting.value) return
  // 旧版提现分支在发请求前先比余额
  if (!isRecharge.value && parseFloat(money.value) > Number(props.user?.balance || 0)) {
    notice.value = '余额不足'
    return
  }
  submitting.value = true
  notice.value = '正在提交，请稍等'
  try {
    const path = isRecharge.value ? '/recharge/create' : '/cash/create'
    const response = await request(path, { method: 'POST', data: { money: money.value, bank: bank.value } })
    // 旧版成功条件是 error_code===0 && data.length>0，error_code 已由 request() 兜住
    if (!(response.data && response.data.length > 0)) {
      notice.value = '提交失败，请稍后再试'
      return
    }
    notice.value = isRecharge.value ? '充值申请提交成功' : '提现申请提交成功'
    // 旧版是先 toast 再 dismiss()，toast 挂在 modal 外面所以还看得见；
    // 这里没有全局 toast，留一点时间让提示可读再关闭
    window.setTimeout(() => emit('close'), 1200)
  } catch (error) {
    notice.value = error.message || '提交失败'
  } finally {
    submitting.value = false
  }
}

// 旧版 ngOnInit
onMounted(() => { void getBankList() })
</script>

<template>
  <div class="money-modal">
    <!-- 旧版 ion-header > ion-toolbar：arrow-back + 标题「上分申请」/「下分申请」 -->
    <header class="modal-header">
      <button class="modal-back" type="button" aria-label="返回" @click="emit('close')">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </button>
      <h1 class="modal-title">{{ directionWord }}分申请</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <!-- 旧版 ion-content[padding]：utilities 里 [padding] 会把四边 --padding-* 设成 16px -->
    <div class="modal-content">
      <form novalidate @submit.prevent="saveData">
        <section class="ion-list">
          <!-- ① 交易类型 -->
          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">交易类型：</div>
              <LegacySelect
                v-model="formType"
                :options="typeOptions"
                alert-header="交易类型："
                cancel-text="取消"
                ok-text="确定"
                @change="onTypeChange"
              />
            </div>
          </div>

          <!-- ② 上/下分方式。bankList 为空时旧版把 select 置灰；
               Ionic 还会给整个 ion-item 加 item-interactive-disabled，
               使 ion-label 也变成 opacity:.3（截图里提现时「下分方式:」就是灰的） -->
          <div class="list-item" :class="{ 'item-interactive-disabled': bankDisabled }">
            <div class="list-item-inner">
              <div class="ion-label">{{ directionWord }}分方式：</div>
              <LegacySelect
                v-model="bank"
                :options="bankOptions"
                :disabled="bankDisabled"
                :alert-header="`${directionWord}分方式：`"
                cancel-text="取消"
                ok-text="确定"
                placeholder="请选择交易方式"
                @change="changBank"
              />
            </div>
          </div>

          <!-- 旧版 Fn：*ngIf="bankValue.value>0 && selectedBank"，绿色银行卡卡片 -->
          <div v-if="Number(bank) > 0 && selectedBank" class="list-item">
            <div class="list-item-inner">
              <div class="bank-item">
                <span class="bank-title">{{ selectedBank.bank_title }}</span>
                <span class="bank-username">{{ selectedBank.bank_username }}</span>
                <span class="bank-account">{{ selectedBank.bank_account }}</span>
              </div>
            </div>
          </div>

          <!-- 旧版 An：充值且该收款方式带图（收款码）时显示 -->
          <div v-if="Number(bank) > 0 && selectedBank && isRecharge && selectedBank.bank_picture_url" class="list-item">
            <div class="list-item-inner">
              <div class="bank-item-img">
                <img class="bank-image" :src="selectedBank.bank_picture_url" alt="" />
              </div>
            </div>
          </div>

          <!-- 旧版 Nn：*ngIf="2==type && user.balance>0" -->
          <div v-if="showBalance" class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">可用金额：</div>
              <div class="money"> {{ fmt2(user.balance) }} </div>
            </div>
          </div>

          <!-- ③ 上/下分金额 -->
          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">{{ directionWord }}分金额：</div>
              <!-- 旧版 ion-input clearInput placeholder="请输入交易金额" type="text" -->
              <div class="ion-input">
                <input v-model="money" class="native-input" type="text" placeholder="请输入交易金额" />
                <!-- clearInput：有值时出现清除按钮 -->
                <button v-if="money" class="input-clear-icon" type="button" aria-label="清除" @click="money = ''"></button>
              </div>
            </div>
          </div>
        </section>

        <!-- 旧版 div.login-action > ion-button color="secondary" expand="full"，文本是「确 定」（中间带空格） -->
        <div class="login-action">
          <button
            class="ion-button button-full button-solid ion-color-secondary"
            type="button"
            :class="{ 'button-disabled': !formValid || submitting }"
            :disabled="!formValid || submitting"
            @click="saveData"
          >
            <span class="button-inner">确 定</span>
          </button>
        </div>

        <!-- 旧版 ion-text[color=warning] > p.warning-tip -->
        <div class="ion-text-warning">
          <p class="warning-tip"> 提示：操作完成后请及时联系管理员或代理。 </p>
        </div>

        <p v-if="notice" class="modal-notice">{{ notice }}</p>
      </form>
    </div>
  </div>
</template>

<style scoped>
/* 弹窗外壳与顶栏跟其它牌九弹窗一致，顶栏色取旧版主题变量 #f95454 */
.money-modal{position:fixed;z-index:30;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}
/* ion-content[padding]：styles.css 的 utilities 把 --padding-* 设成 var(--ion-padding, 16px) */
.modal-content{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:16px}

/* --- Ionic 4 md 的真实默认值，取自 web/135.773ae5d979ae5c36fbef.js --- */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;position:relative;box-sizing:border-box}
/* justify-content 跟着 ion-item 的 .item-native 走 space-between：
   这样 select 那几行会被推到右端，而带 flex:1 的 ion-input 会紧跟在 label 后面 */
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;justify-content:space-between;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}
/* ion-label（md）本身不带 flex，宽度按内容走，不能给 flex:1，
   否则会把后面的 ion-input 挤到最右边（金额那行的 placeholder 就不贴着标签了） */
.ion-label{display:block;font-size:inherit;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}
/* ion-label（md）里的 .item-interactive-disabled .sc-ion-label-md-h{cursor:default;opacity:.3;pointer-events:none} */
.item-interactive-disabled .ion-label{cursor:default;opacity:.3;pointer-events:none}
.money{text-align:right}

/* ion-input（md）。注意：这一组 padding 取的是 Ionic 4 的默认值，
   没能从旧版打包产物里逐条核对（ion-input 在 web/123 里，取样时命令被审批拦下了）。
   如果输入框上下间距对不上，改这里的 padding 即可。 */
.ion-input{display:flex;align-items:center;flex:1;position:relative;padding:10px 0 11px 8px;font-size:inherit}
.ion-input .native-input{flex:1;width:100%;margin:0;padding:0;border:0;outline:none;background:transparent;color:inherit;font:inherit;box-sizing:border-box}
.ion-input .native-input::placeholder{color:currentColor;opacity:.33}
/* clearInput 的清除按钮：Ionic 画的是一个圆形 × */
.input-clear-icon{width:30px;height:30px;border:0;background:transparent;cursor:pointer;padding:0;position:relative;opacity:.5}
.input-clear-icon::before,.input-clear-icon::after{content:"";position:absolute;left:9px;top:14px;width:12px;height:1.5px;background:currentColor}
.input-clear-icon::before{transform:rotate(45deg)}
.input-clear-icon::after{transform:rotate(-45deg)}

/* ion-button color="secondary" expand="full"，取自 web/75（md）：
   host height 36px / font-size 14px / font-weight 500 / letter-spacing .06em / uppercase
   expand="full" → 铺满宽度、去圆角、左右 margin 归零
   secondary → --ion-color-secondary #0cd1e8（见 base.css）
   disabled → opacity .5（截图里那个浅青色就是禁用态） */
.login-action{margin-top:8px}
.ion-button{
  display:flex;align-items:center;justify-content:center;
  width:100%;margin:4px 0;border:0;border-radius:0;
  height:36px;font-size:14px;font-weight:500;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;
  background:var(--ion-color-secondary,#0cd1e8);color:#fff;
  box-shadow:0 3px 1px -2px rgb(0 0 0 / 20%),0 2px 2px 0 rgb(0 0 0 / 14%),0 1px 5px 0 rgb(0 0 0 / 12%);
}
.ion-button.button-disabled{cursor:default;opacity:.5;pointer-events:none;box-shadow:none}
.ion-button .button-inner{display:flex;flex-flow:row nowrap;flex-shrink:0;align-items:center;justify-content:center;width:100%;height:100%}

/* ion-text color="warning" → --ion-color-warning #ffce00 */
.ion-text-warning{color:var(--ion-color-warning,#ffce00)}

/* --- 以下逐条照抄旧版样式表 zn（chunk 19） --- */
.bank-item{height:80px;border-radius:10px;margin-bottom:5px;margin-top:5px;padding:10px;position:relative;background:#62b900;color:#fff;width:100%}
.bank-item .bank-title{font-size:.8rem;display:block;margin-bottom:3px}
.bank-item .bank-username{font-size:.8rem;display:block;position:absolute;right:30px;top:10px}
.bank-item .bank-account{font-size:1.5rem;display:inline-block}
.warning-tip{font-size:.8rem}

/* 旧版没给 .bank-item-img / .bank-image 写样式，这里只保证收款码不溢出 */
.bank-item-img{width:100%;text-align:center}
.bank-image{max-width:100%;display:inline-block}

.modal-notice{margin:10px 0 0;color:#999;font-size:.8rem;text-align:center}
</style>
